import React from 'react';
import { Info, ExternalLink } from 'lucide-react';
import { Select } from '@/components/ui/Select';
import { PortalTooltip } from '@/components/ui/PortalTooltip';

interface TargetFieldWithReferenceProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  reference?: string;
  link?: string;
  options?: { value: string; label: string; }[];
  isInput?: boolean;
  placeholder?: string;
}

export function TargetFieldWithReference({
  id,
  label,
  value,
  onChange,
  reference,
  link,
  options,
  isInput,
  placeholder
}: TargetFieldWithReferenceProps) {
  return (
    <div className="flex flex-col gap-2 w-full">
      <div>
        <label htmlFor={id} className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider mb-2 text-slate-700 dark:text-slate-400">
          {label}
          {reference && (
            <PortalTooltip 
              content={
                <>
                  <span className="font-semibold block mb-1 opacity-90 text-emerald-300">Reference:</span>
                  <span className="leading-relaxed block mb-2 opacity-95 text-slate-200">"{reference}"</span>
                  {link && (
                    link.startsWith('http') ? (
                      <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300 font-medium mt-1">
                        <ExternalLink className="w-3 h-3" />
                        <span className="underline underline-offset-2">View Source</span>
                      </a>
                    ) : (
                      <span className="inline-block mt-1 text-slate-400 font-medium text-[10px]">Source: {link}</span>
                    )
                  )}
                </>
              }
            >
              <Info className="w-3.5 h-3.5 text-slate-400 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300" />
            </PortalTooltip>
          )}
        </label>
        
        {isInput ? (
          <input
            type="text"
            id={id}
            name={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full text-sm border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 bg-white/50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-900 focus:bg-slate-100 dark:focus:bg-slate-900 dark:shadow-inner text-slate-700 dark:text-slate-300 placeholder:text-slate-500 focus:border-slate-200 dark:focus:border-slate-700 focus:ring-1 focus:ring-emerald-500 outline-none"
            placeholder={placeholder}
          />
        ) : (
          <Select
            id={id}
            title={label}
            value={value}
            onChange={onChange}
            className={value === 'Unknown' ? 'text-amber-600 dark:text-amber-500/80 bg-amber-50/50 dark:bg-amber-900/10' : ''}
            options={options || []}
            placeholder=""
          />
        )}
      </div>
    </div>
  );
}
